/**
 * Error classification for the Claude Agent SDK driver.
 *
 * Wraps the Anthropic API error patterns surfaced through the bundled
 * `claude-cli.js`. The CLI flattens upstream HTTP errors into terse strings
 * like:
 *
 *   `Claude Code returned an error result: Failed to authenticate. API Error:
 *    401 {"type":"error","error":{"type":"authentication_error","message":
 *    "Invalid authentication credentials"}}`
 *
 * Word-for-word that reads as an auth failure, but in practice it also
 * fires when a Claude Max OAuth subscription has exhausted its current
 * quota window — Anthropic returns a non-200 that the CLI relabels as
 * "Failed to authenticate" without preserving the original error type.
 *
 * The classifier here:
 *   1. Parses an embedded JSON body when the upstream included one
 *      (e.g. `{"type":"error","error":{"type":"rate_limit_error",...}}`)
 *      so we believe the structured `error.type` over the surrounding
 *      free-text wrapper.
 *   2. Honours the SDK's `terminal_reason` when it explicitly indicates
 *      a blocking-limit / rapid-refill / model-error condition — those
 *      are authoritative even if the textual error reads as auth.
 *   3. Falls back to keyword heuristics on the message text.
 *   4. For OAuth-style credentials (Claude Code subscriptions), adds a
 *      hint that bounces user attention to quota rather than credentials,
 *      because that's overwhelmingly the cause when a CredentialsFile
 *      provider that worked yesterday now returns "Invalid authentication
 *      credentials" without anyone touching the file.
 */

import type { AgentError } from "./types.js";

/**
 * Error thrown by drivers when the upstream provider rejects the credential
 * with an authentication-shaped failure (HTTP 401 / `authentication_error`
 * structured body). Carries the structured {@link AgentError} produced by
 * {@link classifyClaudeSdkError} so the runner can mediate without
 * re-classifying string text.
 *
 * The runner's 401-mediation handler `instanceof`-discriminates on this
 * subclass so the auth-vs-rate_limit fork stays in one place.
 *
 * Spec: `_devlog/specs/2026-05-07-unified-credential-mediation.md`
 * § "Runner-side handling on 401".
 *
 * @category Errors
 * @since 3.3.0
 */
export class AuthError extends Error {
  readonly detail: AgentError;
  constructor(detail: AgentError) {
    super(detail.message);
    this.name = "AuthError";
    this.detail = detail;
  }
}

/**
 * Error thrown when an auth-shaped failure persists *after* a successful
 * credential self-heal — the refresh worked, yet the immediate retry still
 * returned 401. That combination rules out the credential and points at the
 * account itself: a hidden-tier rate limit, a billing block, or anti-abuse
 * throttling on OAuth tokens used at scale (Anthropic mislabels all three as
 * `authentication_error`). It deliberately does **not** extend {@link AuthError}
 * so the driver's auth self-heal does not fire again on it.
 *
 * @category Errors
 */
export class AccountUnavailableError extends Error {
  readonly detail: AgentError;
  constructor(detail: AgentError) {
    super(detail.message);
    this.name = "AccountUnavailableError";
    this.detail = detail;
  }
}

/**
 * Re-label an auth-shaped {@link AgentError} as an `account` failure. Called
 * when a 401 recurs after a successful credential refresh: the credential is
 * fine, so the user needs an account-state hint, not "check your API key".
 * Pure function.
 */
export function reclassifyAuthAsAccountUnavailable(detail: AgentError): AgentError {
  return {
    ...detail,
    category: "account",
    statusCode: detail.statusCode ?? 401,
    retryable: false,
    hint: "The credential was refreshed successfully but Anthropic still rejected the request. This is an account-state issue, not an invalid credential — usually a rate limit at a hidden tier, a billing block, or anti-abuse throttling. Wait a few minutes and retry; if it persists, check the account's billing and usage limits at https://console.anthropic.com/.",
  };
}

/**
 * `terminal_reason` values from the Claude Agent SDK that imply the failure
 * is a rate / quota / blocking condition rather than a credential problem.
 */
const RATE_LIMIT_TERMINAL_REASONS = new Set<string>(["blocking_limit", "rapid_refill_breaker"]);

/**
 * `terminal_reason` values that indicate a generic, retryable model failure.
 * `prompt_too_long` is handled separately (not retryable, different hint).
 */
const MODEL_TERMINAL_REASONS = new Set<string>(["model_error", "image_error"]);

/**
 * Input shape for {@link classifyClaudeSdkError} when decoding an `SDKResultError`.
 *
 * All fields are optional; `classifyClaudeSdkError` also accepts a plain `string`
 * (a thrown exception message) for the common non-SDK code paths.
 *
 * @docLink packages/bridge/api-reference#error-classifier
 */
export interface ClaudeSdkResultErrorInput {
  /** `errors` array from `SDKResultError`. Empty when not provided. */
  errors?: string[];
  /**
   * `terminal_reason` from `SDKResultError`. Authoritative when set to a
   * blocking/rapid-refill value, regardless of message text.
   */
  terminalReason?: string;
  /** `subtype` from `SDKResultError` — `error_max_turns` etc. */
  subtype?: string;
  /**
   * `true` when the configured Anthropic credential is a Claude Code
   * subscription (`CredentialsFile` provider) rather than a raw API key.
   * Changes the hint copy because `Invalid authentication credentials`
   * on an OAuth subscription almost always means quota.
   */
  oauthCredential?: boolean;
}

/**
 * Classify a Claude SDK result-error or thrown error into a structured
 * {@link AgentError}. Pure function — safe to call from any layer.
 *
 * @param input - Either a free-text error message (from a thrown exception)
 *   or a partially-decoded `SDKResultError` shape with `errors[]`,
 *   `terminal_reason`, and `subtype`.
 * @returns A category, retryable flag, and human-readable hint.
 * @docLink packages/bridge/api-reference#error-classifier
 */
export function classifyClaudeSdkError(input: string | ClaudeSdkResultErrorInput): AgentError {
  const opts: ClaudeSdkResultErrorInput = typeof input === "string" ? { errors: [input] } : input;

  const message = (opts.errors ?? []).join("; ") || "Unknown agent error";

  // ── 1. Honour authoritative `terminal_reason` ──────────────────────────
  const byTerminalReason = classifyByTerminalReason(message, opts);
  if (byTerminalReason) {
    return byTerminalReason;
  }

  // ── 2. Inspect embedded JSON body for a structured error.type ──────────
  const structured = tryParseEmbeddedAnthropicError(message);
  if (structured) {
    const byStructured = classifyByStructuredError(message, structured, opts);
    if (byStructured) {
      return byStructured;
    }
  }

  // ── 3. Heuristic keyword classification on the wrapper text ────────────
  const lower = message.toLowerCase();

  const byKeyword = classifyByKeyword(message, lower, opts);
  if (byKeyword) {
    return byKeyword;
  }

  // ── 4. SDK result subtypes ─────────────────────────────────────────────
  const bySubtype = classifyBySubtype(message, opts);
  if (bySubtype) {
    return bySubtype;
  }

  return {
    message,
    category: "unknown",
    retryable: true,
  };
}

/**
 * Phase 1: classify on the SDK's authoritative `terminal_reason`. Returns
 * `null` when the reason is unset or not one of the recognised values.
 */
function classifyByTerminalReason(
  message: string,
  opts: ClaudeSdkResultErrorInput,
): AgentError | null {
  if (opts.terminalReason && RATE_LIMIT_TERMINAL_REASONS.has(opts.terminalReason)) {
    return {
      message,
      category: "rate_limit",
      retryable: true,
      hint: oauthQuotaHint(opts.oauthCredential),
    };
  }
  if (opts.terminalReason === "prompt_too_long") {
    return {
      message,
      category: "model",
      retryable: false,
      hint: "The conversation is too long for the configured model. Start a new session or compact the existing one.",
    };
  }
  if (opts.terminalReason && MODEL_TERMINAL_REASONS.has(opts.terminalReason)) {
    return {
      message,
      category: "model",
      retryable: true,
      hint: "The model rejected this turn. Try again or switch models.",
    };
  }
  return null;
}

/**
 * Phase 2: classify on the structured `error.type` parsed from an embedded
 * Anthropic JSON body. Returns `null` for an unrecognised `errorType`.
 */
function classifyByStructuredError(
  message: string,
  structured: { errorType: string; statusCode?: number },
  opts: ClaudeSdkResultErrorInput,
): AgentError | null {
  if (structured.errorType === "rate_limit_error") {
    return {
      message,
      category: "rate_limit",
      statusCode: structured.statusCode ?? 429,
      retryable: true,
      hint: oauthQuotaHint(opts.oauthCredential),
    };
  }
  if (structured.errorType === "overloaded_error") {
    return {
      message,
      category: "rate_limit",
      statusCode: structured.statusCode ?? 529,
      retryable: true,
      hint: "Anthropic is overloaded. Try again in a moment.",
    };
  }
  if (structured.errorType === "authentication_error") {
    // Anthropic genuinely says it's an auth failure. But for OAuth
    // credentials with the `oauth-2025-04-20` beta flow, this still
    // overwhelmingly correlates with quota. Surface both possibilities
    // in the hint so the user does not first re-upload a perfectly
    // good credentials file.
    return {
      message,
      category: "auth",
      statusCode: structured.statusCode ?? 401,
      retryable: false,
      hint: opts.oauthCredential
        ? "Anthropic rejected the OAuth credential. With Claude Code subscriptions this is usually a quota or rate-limit issue rather than an invalid token — wait a few minutes before re-uploading the credentials file. If it persists, re-export `~/.claude/.credentials.json` from a working Claude Code install."
        : "Anthropic rejected the API key. Check it in Settings -> AI Providers.",
    };
  }
  if (structured.errorType === "permission_error") {
    return {
      message,
      category: "auth",
      statusCode: structured.statusCode ?? 403,
      retryable: false,
      hint: "The configured credential does not have access to the requested model.",
    };
  }
  if (structured.errorType === "invalid_request_error") {
    return {
      message,
      category: "validation",
      statusCode: structured.statusCode ?? 400,
      retryable: false,
      hint: "Anthropic rejected the request shape. This is likely a bug — please report it.",
    };
  }
  return null;
}

/**
 * Phase 3: heuristic keyword classification on the lowercased wrapper text.
 * `lower` is the pre-lowercased `message`. Returns `null` when no bucket hits.
 */
function classifyByKeyword(
  message: string,
  lower: string,
  opts: ClaudeSdkResultErrorInput,
): AgentError | null {
  if (
    lower.includes("rate limit") ||
    lower.includes("rate_limit") ||
    lower.includes("too many requests") ||
    lower.includes("429") ||
    lower.includes("quota") ||
    lower.includes("blocking_limit") ||
    lower.includes("rapid_refill")
  ) {
    return {
      message,
      category: "rate_limit",
      retryable: true,
      hint: oauthQuotaHint(opts.oauthCredential),
    };
  }

  if (
    lower.includes("credit balance") ||
    lower.includes("insufficient credit") ||
    lower.includes("out of credits")
  ) {
    return {
      message,
      category: "rate_limit",
      retryable: false,
      hint: "The configured Anthropic account is out of credits. Top up at https://console.anthropic.com/ or switch providers.",
    };
  }

  if (lower.includes("oauth authentication is currently not supported")) {
    return {
      message,
      category: "auth",
      statusCode: 401,
      retryable: false,
      hint: "The Claude Code CLI did not send the OAuth beta header. The bundled CLI in this image is too old — rebuild the agent image.",
    };
  }

  // The Claude Code CLI emits this terse, JSON-less string when it finds no
  // usable credential — most commonly because the OAuth access token in
  // `~/.claude/.credentials.json` expired and the file carries no refresh token
  // (the platform provisions access-token-only files by design). It MUST be
  // classified as `auth` so the driver promotes it to `AuthError` and the
  // `onAuthError` self-heal re-mints the credential. Without this branch the
  // message falls through to `unknown`, the self-heal never fires, and every
  // subsequent turn dead-ends on "please /login". The check sits ahead of the
  // generic auth keywords because the string contains none of them.
  if (lower.includes("not logged in") || lower.includes("please run /login")) {
    return {
      message,
      category: "auth",
      statusCode: 401,
      retryable: false,
      hint: opts.oauthCredential
        ? "The Claude Code OAuth credential expired and could not be auto-refreshed in-container. The runner will re-mint it — retry the prompt. If it persists, re-export `~/.claude/.credentials.json` from a working Claude Code install."
        : "No Anthropic credential is configured for this provider. Add one in Settings -> AI Providers.",
    };
  }

  if (
    lower.includes("api key") ||
    lower.includes("authentication") ||
    lower.includes("invalid authentication") ||
    lower.includes("unauthorized") ||
    lower.includes("401") ||
    lower.includes("403")
  ) {
    return {
      message,
      category: "auth",
      retryable: false,
      hint: opts.oauthCredential
        ? "Anthropic rejected the OAuth credential. With Claude Code subscriptions this is usually a quota or rate-limit issue rather than an invalid token — wait a few minutes before re-uploading the credentials file."
        : "Check the API key configured for this AI provider in Settings.",
    };
  }

  if (
    lower.includes("model") &&
    (lower.includes("not found") ||
      lower.includes("not available") ||
      lower.includes("does not exist"))
  ) {
    return {
      message,
      category: "model",
      retryable: false,
      hint: "The selected model is not available for this credential. Change the model in the AI provider config.",
    };
  }

  if (
    lower.includes("context length") ||
    lower.includes("token limit") ||
    lower.includes("max_tokens") ||
    lower.includes("prompt is too long") ||
    lower.includes("prompt_too_long")
  ) {
    return {
      message,
      category: "model",
      retryable: false,
      hint: "The conversation is too long for the model. Start a new session or compact this one.",
    };
  }

  if (
    lower.includes("timeout") ||
    lower.includes("econnrefused") ||
    lower.includes("enotfound") ||
    lower.includes("fetch failed") ||
    lower.includes("network")
  ) {
    return {
      message,
      category: "network",
      retryable: true,
      hint: "Network error reaching Anthropic. The runner will retry — try again if it persists.",
    };
  }

  return null;
}

/**
 * Phase 4: classify on the SDK result `subtype` (`error_max_turns` etc).
 * Returns `null` when the subtype is unset or unrecognised.
 */
function classifyBySubtype(message: string, opts: ClaudeSdkResultErrorInput): AgentError | null {
  if (opts.subtype === "error_max_turns") {
    return {
      message,
      category: "unknown",
      retryable: true,
      hint: "The agent stopped after reaching `max_turns`. Resume the session or raise `max_turns` in the agent config.",
    };
  }
  if (opts.subtype === "error_max_budget_usd") {
    return {
      message,
      category: "rate_limit",
      retryable: false,
      hint: "The agent stopped because it hit the configured per-turn budget. Raise the budget or split the work.",
    };
  }
  return null;
}

/**
 * The shared keyword buckets matched by {@link classifyGenericErrorMessage}.
 * Drivers layer a provider-specific `hint` on top of the returned category.
 */
export type GenericErrorCategory = "auth" | "rate_limit" | "model" | "network";

/**
 * Classify a free-text driver error against the keyword buckets common to the
 * subprocess/SDK drivers (omp, codex). Returns the matched `category` +
 * `retryable` flag, or `null` when no shared bucket matches so the caller can
 * fall through to its own provider-specific buckets (e.g. codex sandbox) and
 * `unknown` default. The driver supplies the user-facing `hint` — only the
 * bucketing logic is shared, not the copy.
 */
export function classifyGenericErrorMessage(
  msg: string,
): { category: GenericErrorCategory; retryable: boolean } | null {
  const lower = msg.toLowerCase();
  if (
    lower.includes("api key") ||
    lower.includes("unauthorized") ||
    lower.includes("authentication") ||
    lower.includes("401")
  ) {
    return { category: "auth", retryable: false };
  }
  if (
    lower.includes("rate limit") ||
    lower.includes("429") ||
    lower.includes("too many requests") ||
    lower.includes("quota")
  ) {
    return { category: "rate_limit", retryable: true };
  }
  if (
    lower.includes("model") &&
    (lower.includes("not found") ||
      lower.includes("not available") ||
      lower.includes("does not exist"))
  ) {
    return { category: "model", retryable: false };
  }
  if (
    lower.includes("context length") ||
    lower.includes("too long") ||
    lower.includes("token limit") ||
    lower.includes("max_tokens")
  ) {
    return { category: "model", retryable: false };
  }
  if (
    lower.includes("timeout") ||
    lower.includes("econnrefused") ||
    lower.includes("enotfound") ||
    lower.includes("network") ||
    lower.includes("fetch failed") ||
    lower.includes("fetch") ||
    lower.includes("connection")
  ) {
    return { category: "network", retryable: true };
  }
  return null;
}

/**
 * Returns the rate-limit hint copy, branched on whether the active
 * credential is a Claude Code OAuth subscription.
 */
function oauthQuotaHint(oauthCredential?: boolean): string {
  if (oauthCredential) {
    return "Quota exhausted on this Claude Code subscription. Wait for the rate-limit window to reset (typically a few minutes for short windows, longer for daily/weekly limits) before retrying. The credential itself is fine.";
  }
  return "The Anthropic account hit a rate limit. Wait a moment and try again.";
}

/**
 * Try to extract a structured Anthropic error body from a free-text wrapper.
 * The bundled `claude-cli.js` typically formats failures as
 * `... API Error: <status> {"type":"error","error":{"type":"<errorType>",...}}`
 * with optional trailing garbage after the JSON.
 *
 * Strategy: find the first `{`, then forward-scan tracking brace depth (with
 * string-literal awareness so braces inside quoted values do not throw the
 * count). The substring from `braceStart` to the matching `}` is the JSON
 * candidate; one `JSON.parse` does the rest. O(n) in the input length.
 *
 * Returns `null` when nothing parseable is found.
 */
function tryParseEmbeddedAnthropicError(
  text: string,
): { errorType: string; statusCode?: number } | null {
  const braceStart = text.indexOf("{");
  if (braceStart < 0) {
    return null;
  }

  const braceEnd = findMatchingCloseBrace(text, braceStart);
  if (braceEnd < 0) {
    return null;
  }

  let parsed: { type?: string; error?: { type?: string; message?: string } };
  try {
    parsed = JSON.parse(text.slice(braceStart, braceEnd));
  } catch {
    return null;
  }
  const errorType = parsed?.error?.type;
  if (typeof errorType !== "string") {
    return null;
  }
  const statusMatch = text.match(/API Error:\s*(\d{3})/i);
  const statusCode = statusMatch ? Number(statusMatch[1]) : undefined;
  return { errorType, statusCode };
}

/**
 * Forward-scan `text` from `braceStart` (a `{`) with brace counting, returning
 * the index just past the matching close brace, or `-1` if unbalanced.
 * String-literal awareness: skip braces between unescaped double quotes.
 */
function findMatchingCloseBrace(text: string, braceStart: number): number {
  let depth = 0;
  let inString = false;
  let escaped = false;
  for (let i = braceStart; i < text.length; i++) {
    const ch = text.charCodeAt(i);
    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (ch === 92 /* \ */) {
        escaped = true;
      } else if (ch === 34 /* " */) {
        inString = false;
      }
      continue;
    }
    if (ch === 34 /* " */) {
      inString = true;
    } else if (ch === 123 /* { */) {
      depth++;
    } else if (ch === 125 /* } */) {
      depth--;
      if (depth === 0) {
        return i + 1;
      }
    }
  }
  return -1;
}
